// Client HTTP du panneau chat LLM. POST /api/llm-chat sur la session EC2 active (proxy
// control_plane → Bedrock Claude Haiku 4.5). Cf doc gitlab-profile/docs/architecture/llm-chat.md
// + ADR 034 pour le contexte architectural et les decisions cost/perf.
//
// Pattern symetrique a backend-client.ts : interface + Real + Mock, choix via VITE_BACKEND_MOCK.
// Le frontend envoie l'historique complet a chaque tour (backend stateless). Le system prompt
// (current_world + fleets + waypoints) est construit cote backend a la demande pour rester
// live aux switch-world.

import { PUBLIC_API_URL } from '$env/static/public';
import { auth } from '$lib/auth/auth.svelte';
import { BackendError, SessionNotFoundError, UnauthorizedError } from './errors';

/** Role d'un message dans le chat. Pas de role `system` cote frontend : le system prompt est
 *  construit dynamiquement cote backend a chaque requete. */
export type LlmChatRole = 'user' | 'assistant';

export type LlmChatMessage = {
  readonly role: LlmChatRole;
  readonly content: string;
};

/** Une task dispatchee par le LLM dans le cadre d'une reponse. task_id null = echec dispatch
 *  (le LLM a appele dispatch_task mais le payload a ete rejete par rmf-web). `payload` est
 *  garde pour debug si on veut afficher ce que Claude a genere. */
export type LlmDispatchedTask = {
  readonly task_id: string | null;
  readonly payload: unknown;
  readonly success: boolean;
  readonly error: string | null;
};

export type LlmChatResponse = {
  /** Texte final de l'assistant (1-2 phrases en general). */
  readonly message: string;
  /** Tasks dispatchees pendant la boucle multi-tool-use. Peut etre vide (chitchat). */
  readonly dispatched_tasks: readonly LlmDispatchedTask[];
  /** Tokens input cumules sur la boucle tool-use (incluant system prompt + tool schema +
   *  history + tool results). Sert au compteur cost transparency cote UI. */
  readonly tokens_in: number;
  /** Tokens output cumules (sortie LLM, incluant texte final + tool_use blocks). */
  readonly tokens_out: number;
  /** True si le cap LLM_MAX_DISPATCHES (default 20) a ete atteint : le LLM a vu un signal
   *  `BLOCKED` sur les tool_use restants et a adapte son message ("J'ai stoppe a 20..."). */
  readonly dispatch_cap_reached: boolean;
  /** True si l'historique envoye a Bedrock a ete tronque (> LLM_MAX_HISTORY_MESSAGES, default
   *  30). Le frontend doit avertir l'utilisateur ("ancien contexte partiellement oublie"). */
  readonly history_truncated: boolean;
};

export interface LlmChatClient {
  /**
   * Envoie l'historique complet a l'endpoint LLM, recoit le message d'assistant + les tasks
   * dispatchees. Le caller (chat-store) push le message user dans l'historique AVANT d'appeler,
   * et push le message assistant retourne APRES la response.
   *
   * Throws : `UnauthorizedError` (401), `SessionNotFoundError` (session DNS manquant),
   * `BackendError` (502 Bedrock down, autres 5xx).
   */
  sendMessage(messages: readonly LlmChatMessage[]): Promise<LlmChatResponse>;
}

class RealLlmChatClient implements LlmChatClient {
  /** Cle session DNS dans sessionStorage. Meme convention que RealBackendClient (DNS_KEY).
   *  Re-lue a chaque sendMessage pour gerer les session restarts. */
  private get storedDns(): string | null {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem('rmf:backend:dns');
  }

  async sendMessage(messages: readonly LlmChatMessage[]): Promise<LlmChatResponse> {
    const dns = this.storedDns;
    if (!dns) throw new SessionNotFoundError('No local session DNS for llm-chat');
    const token = await auth.getValidAccessToken();
    const resp = await fetch(`https://${dns}/api/llm-chat`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      // Pas de timeout explicit : Bedrock latence ~1-2s typique, max ~10s en pire cas. Le
      // browser timeout default (~30s) couvre confortablement.
      body: JSON.stringify({ messages: [...messages] })
    });
    if (!resp.ok) throw await mapError(resp);
    return (await resp.json()) as LlmChatResponse;
  }
}

class MockLlmChatClient implements LlmChatClient {
  /** Simule une latence Bedrock + une fausse response. Sert a iterer sur la UI sans backend up.
   *  Le mock dispatche 0 ou 1 task fictive selon le contenu du dernier message user, juste pour
   *  valider l'affichage des `dispatched_tasks` cote UI. */
  async sendMessage(messages: readonly LlmChatMessage[]): Promise<LlmChatResponse> {
    await new Promise((r) => setTimeout(r, 800));
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    const userText = (lastUser?.content ?? '').toLowerCase();
    // Heuristique mock : si l'user mentionne "patrol" ou "task", on simule un dispatch.
    const wantsDispatch = /patrol|task|delivery|clean|dispatch|lance/.test(userText);
    const dispatched: LlmDispatchedTask[] = wantsDispatch
      ? [
          {
            task_id: 'patrol.dispatch-mock-' + Math.random().toString(36).slice(2, 10),
            payload: { category: 'patrol', description: { places: ['mock_wp_1'], rounds: 1 } },
            success: true,
            error: null
          }
        ]
      : [];
    const replyText = wantsDispatch
      ? `[mock] J'ai simule un dispatch patrol. Une fois le backend connecte, ce sera reel.`
      : `[mock] Je suis le LLM en mode mock. Demande-moi de "lancer un patrol" pour voir un dispatch simule.`;
    return {
      message: replyText,
      dispatched_tasks: dispatched,
      tokens_in: 1234,
      tokens_out: 56,
      dispatch_cap_reached: false,
      history_truncated: false
    };
  }
}

/** Map HTTP error response → typed error. Calque le pattern de real-backend-client.mapError. */
async function mapError(resp: Response): Promise<Error> {
  let payload: { detail?: string; message?: string } = {};
  try {
    payload = await resp.json();
  } catch {
    // Pas de body JSON utile (CORS preflight, gateway timeout, etc.).
  }
  const detail = payload.detail ?? payload.message;
  if (resp.status === 401) return new UnauthorizedError(detail);
  if (resp.status === 404) return new SessionNotFoundError(detail);
  if (resp.status === 502) return new BackendError(`LLM unreachable: ${detail ?? 'Bedrock error'}`);
  return new BackendError(`LLM chat ${resp.status}: ${detail ?? resp.statusText}`);
}

const useMock = import.meta.env.VITE_BACKEND_MOCK !== 'false';
export const llmChatClient: LlmChatClient = useMock
  ? new MockLlmChatClient()
  : new RealLlmChatClient();

// Note `PUBLIC_API_URL` est importe pour rester homogene avec backend-client.ts. Le chat LLM
// vise toujours la session DNS (control_plane EC2), pas api.rmf.tf, donc on n'utilise pas
// directement PUBLIC_API_URL. Garde l'import pour clarte d'intention (futur fallback ?).
void PUBLIC_API_URL;
